import React, { useState, useEffect } from 'react';

const AdminDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [toast, setToast] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const csrf = () => document.querySelector('[name="csrf-token"]')?.content;

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => { loadDashboard(); }, []);
  useEffect(() => { if (tab === 'users') loadUsers(1); }, [tab, statusFilter, levelFilter]);

  const loadDashboard = async () => {
    try {
      const res = await fetch('/api/v1/admin/dashboard', {
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() }
      });
      const data = await res.json();
      if (data.success) setStats(data.data?.dashboard);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, per_page: 15 });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (levelFilter) params.append('level', levelFilter);

      const res = await fetch(`/api/v1/admin/users?${params}`, {
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() }
      });
      const data = await res.json();
      if (data.success) {
        setUsers(data.data?.users || []);
        setPagination(data.data?.pagination);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadUsers(1);
  };

  const activateUser = async (userId) => {
    setActionLoading(userId);
    try {
      const res = await fetch(`/api/v1/admin/users/${userId}/activate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() }
      });
      const data = await res.json();
      if (data.success) {
        showToast(data.message);
        loadUsers(pagination?.current_page || 1);
      } else {
        showToast(data.error || 'Erreur', 'error');
      }
    } catch (e) {
      showToast('Erreur réseau', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const deactivateUser = async (userId) => {
    setActionLoading(userId);
    try {
      const res = await fetch(`/api/v1/admin/users/${userId}/deactivate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() }
      });
      const data = await res.json();
      if (data.success) {
        showToast(data.message);
        loadUsers(pagination?.current_page || 1);
      } else {
        showToast(data.error || 'Erreur', 'error');
      }
    } catch (e) {
      showToast('Erreur réseau', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const changeStatusUser = async (userId, status) => {
    setActionLoading(userId);
    try {
      const res = await fetch(`/api/v1/admin/users/${userId}/change_status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() },
        body: JSON.stringify({ status })
      });
      const data = await res.json();
      if (data.success) {
        showToast(data.message);
        loadUsers(pagination?.current_page || 1);
      } else {
        showToast(data.error || 'Erreur', 'error');
      }
    } catch (e) {
      showToast('Erreur réseau', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const changeLevelUser = async (userId, level) => {
    setActionLoading(userId);
    try {
      const res = await fetch(`/api/v1/admin/users/${userId}/change_level`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() },
        body: JSON.stringify({ level })
      });
      const data = await res.json();
      if (data.success) {
        showToast(data.message);
        loadUsers(pagination?.current_page || 1);
      } else {
        showToast(data.error || 'Erreur', 'error');
      }
    } catch (e) {
      showToast('Erreur réseau', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // --- Styles ---
  const colors = {
    primary: '#7c2837',
    primaryDark: '#5a1d28',
    primaryLight: '#a8384e',
    bg: '#fdf8f9',
    card: '#ffffff',
    text: '#2c0e16',
    textMuted: '#6c4a53',
    border: '#e8d0d6',
    success: '#28a745',
    warning: '#ffc107',
    danger: '#dc3545',
    info: '#17a2b8',
  };

  const containerStyle = {
    maxWidth: 1200, margin: '0 auto', padding: '30px 20px',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", color: colors.text, minHeight: '80vh',
  };

  const headerStyle = {
    background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
    color: '#fff', padding: '35px 30px', borderRadius: 16, marginBottom: 30,
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 15,
  };

  const tabBarStyle = {
    display: 'flex', gap: 0, marginBottom: 25, borderBottom: `2px solid ${colors.border}`,
  };

  const tabStyle = (active) => ({
    padding: '12px 28px', cursor: 'pointer', fontWeight: active ? 700 : 500,
    color: active ? colors.primary : colors.textMuted,
    background: 'none', borderTop: 'none', borderLeft: 'none', borderRight: 'none',
    borderBottom: active ? `3px solid ${colors.primary}` : '3px solid transparent',
    fontSize: 15, transition: 'all 0.2s',
  });

  const cardStyle = {
    background: colors.card, borderRadius: 12, padding: 25,
    boxShadow: '0 2px 12px rgba(124,40,55,0.08)', border: `1px solid ${colors.border}`, marginBottom: 20,
  };

  const statCardStyle = (color) => ({
    ...cardStyle, textAlign: 'center', flex: '1 1 180px', minWidth: 160,
    borderTop: `4px solid ${color}`,
  });

  const btnStyle = (variant = 'primary') => {
    const map = {
      primary: { bg: colors.primary, hover: colors.primaryDark, color: '#fff' },
      success: { bg: colors.success, hover: '#218838', color: '#fff' },
      danger: { bg: colors.danger, hover: '#c82333', color: '#fff' },
      outline: { bg: 'transparent', hover: colors.bg, color: colors.primary, border: `1px solid ${colors.primary}` },
    };
    const v = map[variant] || map.primary;
    return {
      padding: '6px 14px', borderRadius: 6, border: v.border || 'none',
      background: v.bg, color: v.color, cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.2s',
    };
  };

  const toastStyle = {
    position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '14px 24px', borderRadius: 10,
    color: '#fff', fontWeight: 600, boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
    background: toast?.type === 'error' ? colors.danger : colors.success,
  };

  const badgeStyle = (color) => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: 12,
    fontSize: 12, fontWeight: 600, color: '#fff', background: color,
  });

  // --- Renders ---
  const renderOverview = () => {
    if (!stats) return <p>Chargement des statistiques...</p>;

    const statCards = [
      { label: 'Membres total', value: stats.users?.total || 0, color: colors.primary, icon: '👥' },
      { label: 'Actifs', value: stats.users?.active || 0, color: colors.success, icon: '✅' },
      { label: 'En attente', value: stats.users?.pending || 0, color: colors.warning, icon: '⏳' },
      { label: 'Cooptations', value: stats.sponsorships?.total || 0, color: colors.info, icon: '🤝' },
      { label: 'Cooptations en attente', value: stats.sponsorships?.pending || 0, color: colors.warning, icon: '📋' },
      { label: 'Récentes (7j)', value: stats.sponsorships?.recent || 0, color: colors.primaryLight, icon: '🔔' },
    ];

    return (
      <div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 30 }}>
          {statCards.map((s, i) => (
            <div key={i} style={statCardStyle(s.color)}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>{s.icon}</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
          {stats.users?.by_level && (
            <div style={{ ...cardStyle, flex: '1 1 300px' }}>
              <h3 style={{ marginTop: 0, color: colors.primary }}>📊 Répartition par niveau</h3>
              {Object.entries(stats.users.by_level).map(([level, count]) => (
                <div key={level} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${colors.border}` }}>
                  <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{level}</span>
                  <span style={badgeStyle(colors.primary)}>{count}</span>
                </div>
              ))}
            </div>
          )}

          {stats.roles?.distribution && (
            <div style={{ ...cardStyle, flex: '1 1 300px' }}>
              <h3 style={{ marginTop: 0, color: colors.primary }}>🎭 Rôles attribués</h3>
              {Object.entries(stats.roles.distribution).map(([role, count]) => (
                <div key={role} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${colors.border}` }}>
                  <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{role.replace(/_/g, ' ')}</span>
                  <span style={badgeStyle(colors.info)}>{count}</span>
                </div>
              ))}
            </div>
          )}

          {stats.sponsorships && (
            <div style={{ ...cardStyle, flex: '1 1 300px' }}>
              <h3 style={{ marginTop: 0, color: colors.primary }}>🤝 Cooptations</h3>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${colors.border}` }}>
                <span style={{ fontWeight: 600 }}>Approuvées</span>
                <span style={badgeStyle(colors.success)}>{stats.sponsorships.approved}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${colors.border}` }}>
                <span style={{ fontWeight: 600 }}>En attente</span>
                <span style={badgeStyle(colors.warning)}>{stats.sponsorships.pending}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                <span style={{ fontWeight: 600 }}>Total</span>
                <span style={badgeStyle(colors.primary)}>{stats.sponsorships.total}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const statusLabels = {
    pending: 'En attente',
    active: 'Actif',
    inactive: 'Inactif',
    suspended: 'Suspendu',
  };

  const statusColors = {
    active: colors.success,
    pending: colors.warning,
    inactive: colors.textMuted,
    suspended: colors.danger,
  };

  const statusBadge = (status) => {
    return <span style={badgeStyle(statusColors[status] || colors.textMuted)}>{statusLabels[status] || status}</span>;
  };

  const levelLabels = {
    invite: 'Invité',
    adherent_sympathisant: 'Sympathisant',
    adherent_participant: 'Participant',
    adherent_actif: 'Actif',
    membre_fondateur: 'Membre Fondateur',
  };

  const levelBadge = (level) => {
    const map = {
      membre_fondateur: colors.primary,
      adherent_actif: colors.success,
      adherent_participant: colors.info,
      adherent_sympathisant: colors.warning,
      invite: colors.textMuted,
    };
    return <span style={badgeStyle(map[level] || colors.textMuted)}>{levelLabels[level] || level}</span>;
  };

  const renderUsers = () => (
    <div>
      <div style={cardStyle}>
        <form onSubmit={handleSearchSubmit} style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 20 }}>
          <input
            type="text" placeholder="Rechercher un membre..." value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: '1 1 200px', padding: '10px 14px', borderRadius: 8, border: `1px solid ${colors.border}`, fontSize: 14 }}
          />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: '10px 14px', borderRadius: 8, border: `1px solid ${colors.border}`, fontSize: 14 }}>
            <option value="">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="pending">En attente</option>
            <option value="inactive">Inactif</option>
            <option value="suspended">Suspendu</option>
          </select>
          <select value={levelFilter} onChange={e => setLevelFilter(e.target.value)}
            style={{ padding: '10px 14px', borderRadius: 8, border: `1px solid ${colors.border}`, fontSize: 14 }}>
            <option value="">Tous les niveaux</option>
            <option value="invite">Invité</option>
            <option value="adherent_sympathisant">Sympathisant</option>
            <option value="adherent_participant">Participant</option>
            <option value="adherent_actif">Actif</option>
            <option value="membre_fondateur">Membre Fondateur</option>
          </select>
          <button type="submit" style={btnStyle('primary')}>🔍 Rechercher</button>
        </form>

        {!users || users.length === 0 ? (
          <p style={{ textAlign: 'center', color: colors.textMuted, padding: 30 }}>Aucun membre trouvé</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${colors.border}`, textAlign: 'left' }}>
                  <th style={{ padding: '10px 8px', color: colors.primary }}>Nom</th>
                  <th style={{ padding: '10px 8px', color: colors.primary }}>Email</th>
                  <th style={{ padding: '10px 8px', color: colors.primary }}>Statut</th>
                  <th style={{ padding: '10px 8px', color: colors.primary }}>Niveau</th>
                  <th style={{ padding: '10px 8px', color: colors.primary }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ padding: '10px 8px', fontWeight: 600 }}>
                      {user.first_name} {user.last_name}
                      {user.nickname && <span style={{ color: colors.textMuted, fontSize: 12 }}> ({user.nickname})</span>}
                    </td>
                    <td style={{ padding: '10px 8px', color: colors.textMuted }}>{user.email}</td>
                    <td style={{ padding: '10px 8px' }}>{statusBadge(user.status)}</td>
                    <td style={{ padding: '10px 8px' }}>{levelBadge(user.level)}</td>
                    <td style={{ padding: '10px 8px' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <select onChange={e => { if (e.target.value) changeStatusUser(user.id, e.target.value); e.target.value = ''; }}
                          style={{ padding: '5px 8px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 12 }}
                          disabled={actionLoading === user.id}>
                          <option value="">Changer statut</option>
                          <option value="active">✅ Actif</option>
                          <option value="pending">⏳ En attente</option>
                          <option value="inactive">⛔ Inactif</option>
                          <option value="suspended">🚫 Suspendu</option>
                        </select>
                        <select onChange={e => { if (e.target.value) changeLevelUser(user.id, e.target.value); e.target.value = ''; }}
                          style={{ padding: '5px 8px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 12 }}
                          disabled={actionLoading === user.id}>
                          <option value="">Changer niveau</option>
                          <option value="invite">Invité</option>
                          <option value="adherent_sympathisant">Sympathisant</option>
                          <option value="adherent_participant">Participant</option>
                          <option value="adherent_actif">Actif</option>
                          <option value="membre_fondateur">Membre Fondateur</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination && pagination.total_pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
            {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => loadUsers(p)}
                style={{
                  ...btnStyle(p === pagination.current_page ? 'primary' : 'outline'),
                  minWidth: 36,
                }}>
                {p}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ ...containerStyle, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
          <p style={{ color: colors.textMuted, fontSize: 16 }}>Chargement du dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {toast && <div style={toastStyle}>{toast.message}</div>}

      <div style={headerStyle}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28 }}>📊 Dashboard Administration</h1>
          <p style={{ margin: '8px 0 0', opacity: 0.85, fontSize: 14 }}>
            Gestion des membres et de l'association
          </p>
        </div>
        <a href="/options" style={{ ...btnStyle('outline'), color: '#fff', borderColor: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>
          ← Retour aux paramètres
        </a>
      </div>

      <div style={tabBarStyle}>
        <button style={tabStyle(tab === 'overview')} onClick={() => setTab('overview')}>📈 Vue d'ensemble</button>
        <button style={tabStyle(tab === 'users')} onClick={() => setTab('users')}>👥 Membres</button>
      </div>

      {tab === 'overview' && renderOverview()}
      {tab === 'users' && renderUsers()}
    </div>
  );
};

export default AdminDashboardPage;
